import React from "react";
import PropTypes from "prop-types";
import { buildScopeTranslate } from "lib/I18n";
import StatsBar from "./StatsBar";
import AssessmentLocked from "./AssessmentLocked";
import { LOCKED_STATES } from "../constants";
import { assessmentCopyKey, refPropType } from "../utils";

const t = buildScopeTranslate("views.content.assessment_framework.assessments");

const LOCKED_STATE_VALUES = Object.values(LOCKED_STATES);

function getPassPercent(passingScore) {
  return Math.round((passingScore || 0) * 100);
}

const AssessmentFrameworkIntroScreen = ({
  presentation,
  lockedState = null,
  onStart,
  starting = false,
  attested = false,
  onAttestChange,
  headingRef = null,
  testOut = null,
  showUnassignedNotice = false,
  showPreviewNotice = false,
}) => {
  if (!presentation) {
    return null;
  }

  const passPercent = getPassPercent(presentation.passing_score);
  const leadKey = `pages.intro.${assessmentCopyKey("lead", testOut)}`;
  const statItems = [
    {
      icon: "task",
      text: t("labels.questions_count", { count: presentation.question_count }),
    },
    {
      icon: "success",
      text: t("labels.passing_score", { score: passPercent }),
    },
  ];

  if (presentation.estimated_duration_mins) {
    statItems.push({
      icon: "clock",
      text: t("labels.estimated_time", { time: presentation.estimated_duration_mins }),
    });
  }

  const lockedKey = LOCKED_STATE_VALUES.includes(lockedState) ? lockedState : null;

  return (
    <div className="th-agentforce-assessment__intro th-agentforce-assessment__assessment-framework-intro">
      <div className="slds-text-align_center th-agentforce-assessment__assessment-framework-intro-header">
        <h1
          ref={headingRef}
          className="th-agentforce-assessment__intro-title"
          tabIndex="-1">
          {presentation.title}
        </h1>
        <p className="th-agentforce-assessment__intro-lead">
          {t(leadKey, { score: passPercent })}
        </p>
        {showUnassignedNotice && (
          <p className="th-agentforce-assessment__intro-unassigned-notice">
            {t("pages.intro.unassigned_notice")}
          </p>
        )}
        {showPreviewNotice && (
          <p className="th-agentforce-assessment__intro-preview-notice">
            {t("pages.intro.preview_notice")}
          </p>
        )}
      </div>

      <div className="th-agentforce-assessment__stat-bar-pill">
        <StatsBar items={statItems} />
      </div>

      {presentation.description && (
        <p className="th-agentforce-assessment__intro-description">
          {presentation.description}
        </p>
      )}

      {lockedKey ? (
        <AssessmentLocked lockedKey={lockedKey} />
      ) : (
        <>
          <label className="th-agentforce-assessment__intro-attestation">
            <input
              type="checkbox"
              className="th-agentforce-assessment__intro-attestation-control"
              checked={attested}
              onChange={(event) => onAttestChange(event.target.checked)}
            />
            <span className="th-agentforce-assessment__intro-attestation-copy">
              {t("pages.intro.attestation.label")}
            </span>
          </label>
          <tds-button
            variant="brand"
            size="large"
            onClick={onStart}
            disabled={starting || !attested}
            spinner={starting}>
            <span>{t("pages.intro.buttons.primary")}</span>
          </tds-button>
        </>
      )}
    </div>
  );
};

AssessmentFrameworkIntroScreen.propTypes = {
  presentation: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    question_count: PropTypes.number.isRequired,
    passing_score: PropTypes.number.isRequired,
    estimated_duration_mins: PropTypes.number,
  }).isRequired,
  lockedState: PropTypes.oneOf(LOCKED_STATE_VALUES),
  onStart: PropTypes.func.isRequired,
  starting: PropTypes.bool,
  attested: PropTypes.bool,
  onAttestChange: PropTypes.func.isRequired,
  headingRef: refPropType,
  testOut: PropTypes.bool,
  showUnassignedNotice: PropTypes.bool,
  showPreviewNotice: PropTypes.bool,
};

export default AssessmentFrameworkIntroScreen;
